import * as React from "react";

import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";

import {
  AccessRadiusSearchPageElementEnum,
  AccessReportsAccessAnalysisElementEnum,
  AccessReportsCoverPageElementEnum,
  AccessReportsPageOptionsElementEnum,
  AccessReportsServiceArea,
  AccessReportsWizardAccessCriteria,
  AccessReportsWizardElement,
  AccessReportsWizardElementTypeEnum,
  AccessReportsWizardFormGroup,
  AccessReportsWizardGroupPropertyIdEnum,
  AccessReportsWizardNetworkStandard,
  AccessReportsWizardNetworkStandardTypeEnum,
  AccessReportsWizardOption,
  AccessReportsWizardPage,
  AccessReportsWizardProduct,
  AccessReportsWizardProviderSet,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
  ByProductDefault,
  ColumnDefaults,
  EmployeeGroupStatusEnum,
  PageSelectionElementIdEnum,
} from "@app/access-reports/shared";
import {
  AccessOverviewEditChartColorsElementEnum,
  AccessReportsAdminAddProductDetails,
  AccessReportsAdminAddProductPageOptions,
  AccessReportsAdminAddProductSpecialties,
  AccessReportsAdminAddProductStepEnum,
  AccessReportsAdminPageOptionsEnum,
  AccessReportsAdminWizardStep,
  AccessSummaryEditChartColorsElementEnum,
} from "@app/access-reports/admin";
import { AccessReportProviderSetModel } from "@app/provider-sets";
import { cloneDeep, flatten, isNil } from "lodash";
import { getAllElementsByPageFromTemplate, getElementByIdFromTemplate, getFormGroupByGroupPropertyIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { LoadingSpinner } from "../../shared";

type AccessReportsAdminAddProductProps = RouteComponentProps<unknown> & {
  productId: number;
  disableUserActions: boolean;
  onSaveProductClick: (template: AccessReportsWizardTemplate) => void;
  template: AccessReportsWizardTemplate;
}

interface AccessReportsAdminAddProductState {
  filterMemberSetByOutsideServiceArea: boolean;
  groups: string[];
  hiddenAccessStandards: number[];
  hiddenCalculationMethods: number[];
  hiddenDistanceMethods: number[];
  invalidElements: Map<string, string>;
  invalidPageOptionPageElements: Map<string, string>;
  product: AccessReportsWizardProduct;
  productType: AccessReportsWizardOption;
  productTypes: AccessReportsWizardOption[];
  reportTitleOne: string;
  reportTitleThree: string;
  reportTitleTwo: string;
  selectedMemberSetServiceArea: AccessReportsServiceArea;
  template: AccessReportsWizardTemplate;
}

class AccessReportsAdminAddProduct extends React.Component<AccessReportsAdminAddProductProps, AccessReportsAdminAddProductState> {
  constructor(props: AccessReportsAdminAddProductProps) {
    super(props);

    this.state = {
      filterMemberSetByOutsideServiceArea: false,
      groups: [],
      hiddenAccessStandards: [],
      hiddenCalculationMethods: [],
      hiddenDistanceMethods: [],
      invalidElements: new Map<string, string>(),
      invalidPageOptionPageElements: new Map<string, string>(),
      product: null,
      productType: null,
      productTypes: [],
      reportTitleOne: "",
      reportTitleThree: "",
      reportTitleTwo: "",
      selectedMemberSetServiceArea: null,
      template: null,
    };
  }

  public componentDidMount() {
    if (!!this.props.template) {
      this.extractProductDefaults();
    }
  }

  public componentDidUpdate(prevProps: AccessReportsAdminAddProductProps) {
    if (!prevProps.template && !!this.props.template) {
      this.extractProductDefaults();
    }
  }

  public render() {
    return (
      <Switch>
        <Route
          exact={true}
          path={`${this.props.match.url}/step/1`}
          render={() => this.createWizardStep(AccessReportsAdminAddProductStepEnum.ProductDetails, 0, `/access-reports/admin/product/${this.state.product?.id}/step/2`)}
        />
        <Route
          exact={true}
          path={`${this.props.match.url}/step/2`}
          render={() => this.createWizardStep(AccessReportsAdminAddProductStepEnum.Specialties, 1, `/access-reports/admin/product/${this.state.product?.id}/step/3`, null, `/access-reports/admin/product/${this.state.product?.id}/step/1`)}
        />
        <Route
          exact={true}
          path={`${this.props.match.url}/step/3`}
          render={() => this.createWizardStep(AccessReportsAdminAddProductStepEnum.PageOptions, 2, null, this.onSaveProductClick, `/access-reports/admin/product/${this.state.product?.id}/step/2`)}
        />
      </Switch>
    );
  }

  public checkReportTitleValidity = (reportTitle: string): boolean => {
    // returns whether a report title string contains the following "invalid" characters:  / \ : . * ? " < > |
    return !/[/\\:.*\\?"<>|]/.test(reportTitle);
  }

  public getSpecialtyValidity = (accessCriteria: AccessReportsWizardAccessCriteria): boolean => {
    // only numbers are considered to be valid for standards
    for (const standard of accessCriteria.standards) {
      for (const value of Object.values(standard)) {
        if (!value || value?.toString() === "0" || value?.toString() === ".") {
          return false;
        }
      }
    }

    // each specialty must have a valid provider set id attached to it
    if (!accessCriteria.providerSet ||
      accessCriteria.providerSet.providerSetId === null || accessCriteria.providerSet.providerSetId === undefined
    ) {
      return false
    }

    return true;
  }

  public onAccessAnalysisDefaultChange = (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const element: AccessReportsWizardElement = getElementByIdFromTemplate(template, AccessReportsWizardStepEnum.AccessAnalysis, accessAnalysis);

    if (!!element?.options) {
      for (const option of element.options) {
        if (option.id === optionId && !option.defaultProducts.includes(this.state.product.id)) {
          option.defaultProducts.push(this.state.product.id);
        } else {
          option.defaultProducts = option.defaultProducts.filter(x => x !== this.state.product.id);
        }
      }

      this.setState({
        template,
      })
    }
  }

  public onAccessAnalysisHideChange = (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum): void => {
    switch (accessAnalysis) {
      case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
        let hiddenCalculationMethods: number[] = cloneDeep(this.state.hiddenCalculationMethods);

        if (hiddenCalculationMethods.includes(optionId)) {
          hiddenCalculationMethods = hiddenCalculationMethods.filter(x => x !== optionId);
        } else {
          hiddenCalculationMethods.push(optionId);
        }

        this.setState({
          hiddenCalculationMethods,
        });
        break;
      case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
        let hiddenDistanceMethods: number[] = cloneDeep(this.state.hiddenDistanceMethods);

        if (hiddenDistanceMethods.includes(optionId)) {
          hiddenDistanceMethods = hiddenDistanceMethods.filter(x => x !== optionId);
        } else {
          hiddenDistanceMethods.push(optionId);
        }

        this.setState({
          hiddenDistanceMethods,
        });
        break;
      case AccessReportsAccessAnalysisElementEnum.AccessStandard:
        let hiddenAccessStandards: number[] = cloneDeep(this.state.hiddenAccessStandards);

        if (hiddenAccessStandards.includes(optionId)) {
          hiddenAccessStandards = hiddenAccessStandards.filter(x => x !== optionId);
        } else {
          hiddenAccessStandards.push(optionId);
        }

        this.setState({
          hiddenAccessStandards,
        });
        break;
      default:
        console.error("Invalid Access Analysis Element Type");
        break;
    }
  }

  public onAddGroupClick = (group: string): void => {
    const groups: string[] = cloneDeep(this.state.groups);
    groups.push(group);

    this.setState({
      groups,
    });
  }

  private createByProductDefault = (
    pageFormGroup: AccessReportsWizardFormGroup,
    selected: boolean,
    productId: number,
  ): ByProductDefault => {
    const byProductDefault: ByProductDefault = {
      id: pageFormGroup.byProductDefaults.length > 0 ? Math.max(...pageFormGroup.byProductDefaults.map((x) => x.id)) + 1 : 0,
      productId,
    };

    let chartColorIds: AccessSummaryEditChartColorsElementEnum[] | AccessOverviewEditChartColorsElementEnum[] = [];
    const setPage: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === productId);

    switch (pageFormGroup.groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        chartColorIds = Object.values(AccessSummaryEditChartColorsElementEnum);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        chartColorIds = Object.values(AccessOverviewEditChartColorsElementEnum);
        break;
      default:
        break;
    }

    for (const element of pageFormGroup.elements) {
      if ((chartColorIds as string[]).includes(element.elementId)) {
        // if a page of this type already exists, use those values to populate admin-specific defaults
        if (!!setPage) {
          byProductDefault[element.elementId] = setPage[element.elementId];
        } else {
          // otherwise, use the defaults from the template form group node 
          if (!!(element.defaultValue.toString())) {
            byProductDefault[element.elementId] = element.defaultValue;
          }
        }
      } else {
        switch (element.elementType) {
          case AccessReportsWizardElementTypeEnum.Checkbox:
            if (Object.values(PageSelectionElementIdEnum).includes(element.elementId as PageSelectionElementIdEnum)) {
              byProductDefault[element.elementId] = selected;
            } else {
              byProductDefault[element.elementId] = false;
            }
            break;
          case AccessReportsWizardElementTypeEnum.ColorPicker:
          case AccessReportsWizardElementTypeEnum.DatePicker:
          case AccessReportsWizardElementTypeEnum.Dropdown:
          case AccessReportsWizardElementTypeEnum.Radio:
          case AccessReportsWizardElementTypeEnum.Unknown:
            byProductDefault[element.elementId] = null;
            break;
          case AccessReportsWizardElementTypeEnum.Input:
            byProductDefault[element.elementId] = "";
            break;
          default:
            break;
        }
      }
    }

    return byProductDefault;
  }

  public getNewAccessCriterion = (name: string, id: number, includeStandards: boolean): AccessReportsWizardAccessCriteria => {
    return {
      defaultProducts: [],
      displayName: name,
      filterMemberSetByOutsideServiceArea: false,
      filterMemberSetByServiceArea: false,
      filterProviderSetByOutsideServiceArea: false,
      filterProviderSetByServiceArea: false,
      groups: [],
      hidden: false,
      hiddenProducts: [],
      id,
      memberSet: {
        employeeGroupStatus: EmployeeGroupStatusEnum.Unknown,
        fileName: "",
        isCensus: false,
        memberSetId: 0,
        memberSetName: "",
        memberSetVersionId: 0,
      },
      memberSetServiceArea: null,
      name,
      providerSet: null,
      providerSetServiceArea: null,
      selected: false,
      standards: includeStandards ? Object.values(AccessReportsWizardNetworkStandardTypeEnum).map((x) => {
        return this.getAccessStandardDefaultValues(x);
      }) : [],
    };
  }

  public onAddPageClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      let selected = false;
      const firstByProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === this.state.product.id);

      if (!!firstByProductDefault) {
        for (const property of Object.keys(firstByProductDefault)) {
          if (Object.values(PageSelectionElementIdEnum).includes(property as PageSelectionElementIdEnum)) {
            selected = firstByProductDefault[property] as boolean;
            break;
          }
        }
      }

      const byProductDefault: ByProductDefault = this.createByProductDefault(pageFormGroup, selected, this.state.product.id);

      pageFormGroup.byProductDefaults.push(byProductDefault);

      this.setState({
        template,
      })
    }
  }

  public onAddSpecialtiesClick = (specialties: string[]): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);

    for (const specialty of specialties) {
      let maxId = 0;
      if (!!product && product.accessCriterion.length) {
        maxId = Math.max(...product.accessCriterion.map((o) => o.id)) + 1;
      }

      const accessCriteria: AccessReportsWizardAccessCriteria = this.getNewAccessCriterion(specialty, maxId, true);

      product.accessCriterion.push(accessCriteria);
    }

    this.setState({
      product,
    });
  }

  public onAllowEditToggle = (): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    product.editable = !product.editable;

    this.setState({
      product,
    });
  }

  public onDisclaimerTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions);
    const disclaimerPage: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);
    const productOption: AccessReportsWizardOption = disclaimerPage?.options?.find(x => x.id === this.state.product.id);

    if (!!productOption) {
      productOption.name = event.target.value;

      this.setState({
        template,
      });
    }
  }

  public onFilterMemberSetByOutsideServiceAreaToggle = (): void => {
    this.setState({
      filterMemberSetByOutsideServiceArea: !this.state.filterMemberSetByOutsideServiceArea,
    });
  }

  public onFilterProviderSetByOutsideServiceAreaToggle = (accessCriteriaId: number): void => {
    if (!!this.state.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
      const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
      const accessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        accessCriteria.filterProviderSetByOutsideServiceArea = !accessCriteria.filterProviderSetByOutsideServiceArea;

        this.setState({
          product,
        });
      }
    }
  }

  public onGroupToggle = (accessCriteriaId: number, group: string): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    const accessCriteria = product.accessCriterion.find(x => x.id === accessCriteriaId);

    if (accessCriteria) {
      if (accessCriteria.groups.includes(group)) {
        accessCriteria.groups = accessCriteria.groups.filter(x => x !== group);
      } else {
        accessCriteria.groups.push(group);
      }
    }

    this.setState({
      product,
    });
  }

  public onNetworkStandardChange = (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: "minCount" | "miles" | "minutes",
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = accessReportsWizardAccessCriteria.standards.find((x) => x.type === networkStandardType);

      if (!!standard) {
        if (!!event) {
          // on change
          // ensure that only numbers may be input
          const value: number = parseInt(event.target.value);

          if ((!isNaN(value) && event.target.value !== "0") || !event.target.value) {
            (standard)[standardKey] = value;

            this.setState({
              product,
            });
          }
        } else {
          // on blur
          if (!(standard)[standardKey] || ((standard)[standardKey] as number).toString() === "0") {
            // assign min value
            (standard)[standardKey] = 1;

            this.setState({
              product,
            });
          }
        }
      }
    }
  }

  public onNetworkStandardMilesChange = (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: "miles",
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = accessReportsWizardAccessCriteria.standards.find((x) => x.type === networkStandardType);

      if (!!standard) {
        if (!!event) {
          // on change
          const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

          // ensure that only float numbers may be input
          if (re.test(event.target.value)) {
            let updateValue: string = event.target.value;
            const value: number = parseFloat(event.target.value);

            if (value >= 1) {
              updateValue = updateValue.replace(/^0+/, "");   // remove leading zeros
            }

            if (!isNaN(value) || !event.target.value || event.target.value === ".") {
              (standard)[standardKey] = parseFloat(updateValue);

              this.setState({
                product,
              });
            }


          }
        } else {
          // on blur
          if (!(standard)[standardKey] ||
            ((standard)[standardKey]).toString() === "0" ||
            ((standard)[standardKey]).toString() === "." ||
            ((standard)[standardKey]).toString() === "0."
          ) {
            // assign min value
            (standard)[standardKey] = .1;

            this.setState({
              product,
            });
          }
        }
      }
    }
  }

  public onPageOptionToggle = (elementId: string, key: AccessReportsAdminPageOptionsEnum): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions);
    const pageOption: AccessReportsWizardElement = pageOptionsFormGroup.elements.find(x => x.elementId === elementId);
    const pageOptionProductOption: AccessReportsWizardOption = pageOption?.options.find(x => x.id === this.state.product.id);

    if (!!pageOptionProductOption) {
      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          if (!pageOptionProductOption.defaultProducts?.includes(this.state.product.id)) {
            pageOptionProductOption.defaultProducts = [this.state.product.id];
          } else {
            pageOptionProductOption.defaultProducts = [];
          }

          break;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (!pageOptionProductOption.hiddenProducts?.includes(this.state.product.id)) {
            pageOptionProductOption.hiddenProducts = [this.state.product.id];
          } else {
            pageOptionProductOption.hiddenProducts = [];
          }

          break;
        default:
          console.error("Invalid Admin Page Option");
          break;
      }

      this.setState({
        template,
      });
    }
  }

  public onPageOptionPageChange = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number, elementId: string, value: string | number | boolean): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const invalidPageOptionPageElements: Map<string, string> = cloneDeep(this.state.invalidPageOptionPageElements);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);
    const byProductDefault: ByProductDefault = pageFormGroup?.byProductDefaults.find(x => x.id === byProductDefaultId && x.productId === this.state.product.id);

    if (!!byProductDefault) {
      byProductDefault[elementId] = value;
    }

    if (groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch) {
      const getElementValue = (elementId: string): number => {
        return parseFloat(byProductDefault[elementId]?.toString());
      }

      const handleMapSetting = (mapElementId: string, isInvalid: boolean) => {
        const mappingKey: string = byProductDefault.id + mapElementId;
        const alreadyExistingMapping: string = invalidPageOptionPageElements.get(mappingKey);

        if (isInvalid && !alreadyExistingMapping) {
          invalidPageOptionPageElements.set(mappingKey, "Radii values must be ascending");
        } else if (!!alreadyExistingMapping && !isInvalid) {
          invalidPageOptionPageElements.delete(mappingKey);
        }
      }

      const radiiOne: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiOne);
      const radiiTwo: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiTwo);
      const radiiThree: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiThree);
      const radiiFour: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFour);
      const radiiFive: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFive);

      let radiiOneInvalid = false;
      let radiiTwoInvalid = false;
      let radiiThreeInvalid = false;
      let radiiFourInvalid = false;
      let radiiFiveInvalid = false;

      if (radiiOne >= radiiTwo || radiiOne >= radiiThree || radiiOne >= radiiFour || radiiOne >= radiiFive) {
        radiiOneInvalid = true;
      }

      if (radiiTwo <= radiiOne || radiiTwo >= radiiThree || radiiTwo >= radiiFour || radiiTwo >= radiiFive) {
        radiiTwoInvalid = true;
      }

      if (radiiThree <= radiiOne || radiiThree <= radiiTwo || radiiThree >= radiiFour || radiiThree >= radiiFive) {
        radiiThreeInvalid = true;
      }

      if (radiiFour <= radiiOne || radiiFour <= radiiTwo || radiiFour <= radiiThree || radiiFour >= radiiFive) {
        radiiFourInvalid = true;
      }

      if (radiiFive <= radiiOne || radiiFive <= radiiTwo || radiiFive <= radiiThree || radiiFive <= radiiFour) {
        radiiFiveInvalid = true;
      }

      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiOne, radiiOneInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiTwo, radiiTwoInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiThree, radiiThreeInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFour, radiiFourInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFive, radiiFiveInvalid);
    }

    this.setState({
      invalidPageOptionPageElements,
      template,
    });
  }

  public onPageOptionPageToggle = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum,): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    switch (key) {
      case AccessReportsAdminPageOptionsEnum.Default:
        const byProductDefaults: ByProductDefault[] = pageFormGroup.byProductDefaults.filter(x => x.productId === this.state.product.id);

        for (const byProductDefault of byProductDefaults) {
          const selectionKey = Object.keys(byProductDefault).find(x => Object.values(PageSelectionElementIdEnum).includes(x as PageSelectionElementIdEnum))
          byProductDefault[selectionKey] = !byProductDefault[selectionKey] as boolean;
        }

        break;
      case AccessReportsAdminPageOptionsEnum.Hide:
        if (pageFormGroup.hiddenProducts.includes(this.state.product.id)) {
          pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== this.state.product.id);
        } else {
          pageFormGroup.hiddenProducts.push(this.state.product.id);
        }

        break;
      case AccessReportsAdminPageOptionsEnum.AllowMultiple:
        if (pageFormGroup.disallowedMultipleProducts.includes(this.state.product.id)) {
          pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== this.state.product.id);
        } else {
          pageFormGroup.disallowedMultipleProducts.push(this.state.product.id);
        }

        break;
      default:
        console.error("Invalid Admin Page Option");
        break;
    }

    this.setState({
      template,
    });
  }

  public onProductInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    product.displayName = event.target.value;
    product.name = event.target.value;

    this.setState({
      product,
    });
  }

  public onProductTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.props.template);

    if (!!template) {
      const coverPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productTypeElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.ProductType);

        if (!!productTypeElement && !!productTypeElement.options) {
          const productType: AccessReportsWizardOption = productTypeElement.options.find(x => x.id.toString() === event.target.value);

          this.setState({
            productType,
          });
        }
      }
    }
  }

  public onProviderGroupChange = (accessCriteria: AccessReportsWizardAccessCriteria, providerSetModel: AccessReportProviderSetModel): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const providerSet: AccessReportsWizardProviderSet = {
        providerSetId: providerSetModel.providerSetId,
        providerSetName: providerSetModel.name,
        providerSetVersionId: providerSetModel.versions[0]?.providerSetVersionId,
      }

      accessReportsWizardAccessCriteria.providerSet = providerSet;

      this.setState({
        product,
      });
    }
  }

  public onReportTitleInputChange = (reportTitleElement: AccessReportsCoverPageElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const invalidElements: Map<string, string> = this.state.invalidElements;

    const inputsWithValidation: AccessReportsCoverPageElementEnum[] = [
      AccessReportsCoverPageElementEnum.ReportTitleOne,
      AccessReportsCoverPageElementEnum.ReportTitleTwo,
      AccessReportsCoverPageElementEnum.ReportTitleThree,
    ];

    if (inputsWithValidation.includes(reportTitleElement)) {
      const invalidReportTitleString = `Your report title may not contain any of the following characters: \u002f \u005c \u003a \u002e \u002a \u003f \u0022 \u003c \u003e |`;
      const alreadyExistingMapping: string = invalidElements.get(reportTitleElement);
      const isValid: boolean = this.checkReportTitleValidity(event.target.value);

      if (isValid) {
        if (!!alreadyExistingMapping) {
          invalidElements.delete(reportTitleElement);
        }
      } else {
        if (!alreadyExistingMapping) {
          invalidElements.set(reportTitleElement, invalidReportTitleString);
        }
      }
    }

    switch (reportTitleElement) {
      case AccessReportsCoverPageElementEnum.ReportTitleOne:
        this.setState({
          invalidElements,
          reportTitleOne: event.target.value,
        })
        break;
      case AccessReportsCoverPageElementEnum.ReportTitleTwo:
        this.setState({
          invalidElements,
          reportTitleTwo: event.target.value,
        });
        break;
      case AccessReportsCoverPageElementEnum.ReportTitleThree:
        this.setState({
          invalidElements,
          reportTitleThree: event.target.value,
        });
        break;
      default:
        console.error(`Invalid Report Title Element Type: ${reportTitleElement}`);
        break;
    }
  }

  public onRemoveFormGroupClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      pageFormGroup.byProductDefaults = pageFormGroup.byProductDefaults.filter(x => x.id !== byProductDefaultId);
    }

    this.setState({
      template,
    });
  }

  public onRemoveGroupClick = (group: string): void => {
    const groupsClone: string[] = cloneDeep(this.state.groups);
    const groups: string[] = groupsClone.filter(x => x !== group);
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);

    // if editing a product, remove the group from every access criteria it may have already been applied to
    for (const accessCriteria of product.accessCriterion) {
      if (accessCriteria.groups) {
        if (accessCriteria.groups.includes(group)) {
          accessCriteria.groups = accessCriteria.groups.filter(x => x !== group);
        }
      }
    }

    this.setState({
      groups,
      product,
    });
  }

  public onRemoveSpecialtyClick = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    product.accessCriterion = product.accessCriterion.filter(x => x.id !== accessCriteria.id);

    // adjust the id values so there are no gaps
    for (let i = 0; i < product.accessCriterion.length; i++) {
      product.accessCriterion[i].id = i;
    }

    // prevent the next closest specialty accordion panel from automatically opening
    $('#access-reports-admin__specialties--accordion .collapse').attr("data-parent", "#access-reports-admin__specialties--accordion").collapse('hide');

    this.setState({
      product,
    });
  }

  public onSaveAccessOverviewChartColorsClick = (byProductDefault: ByProductDefault): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages);

    if (!!pageFormGroup) {
      const byProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === this.state.product.id);

      for (const bpd of byProductDefaults) {
        for (const key of Object.keys(byProductDefault)) {
          if (Object.values(AccessOverviewEditChartColorsElementEnum).includes(key as AccessOverviewEditChartColorsElementEnum)) {
            bpd[key] = byProductDefault[key];
          }
        }
      }

      this.setState({
        template,
      });
    }
  }

  public onSaveAccessSummaryChartColorsClick = (byProductDefault: ByProductDefault): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages);

    if (!!pageFormGroup) {
      const byProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === this.state.product.id);

      for (const bpd of byProductDefaults) {
        for (const key of Object.keys(byProductDefault)) {
          if (Object.values(AccessSummaryEditChartColorsElementEnum).includes(key as AccessSummaryEditChartColorsElementEnum)) {
            bpd[key] = byProductDefault[key];
          }
        }
      }

      this.setState({
        template,
      });
    }
  }

  public onSaveColumnsClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const columnDefault: ColumnDefaults = pageFormGroup.columnDefaults.find(x => x.productId === this.state.product.id);

      if (!!columnDefault) {
        const index: number = pageFormGroup.columnDefaults.indexOf(columnDefault);
        pageFormGroup.columnDefaults[index] = columnDefaults;
      } else {
        pageFormGroup.columnDefaults.push(columnDefaults);
      }

      this.setState({
        template,
      });
    }
  }

  public onSaveProductClick = (): void => {
    const template: AccessReportsWizardTemplate = this.state.template;

    if (!!template) {
      const accessAnalysisPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);
      const coverPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const product: AccessReportsWizardProduct = this.state.product;

      if (!!accessAnalysisPage && !product.skipAccessAnalysis) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

        for (const element of accessAnalysisPageElements) {
          switch (element.elementId) {
            case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(product.id)) {
                  // if this calculation method option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!this.state.hiddenCalculationMethods.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== product.id);
                  }
                } else {
                  // if this calculation method option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (this.state.hiddenCalculationMethods.includes(option.id)) {
                    option.hiddenProducts.push(product.id);
                  }
                }
              }

              break;
            case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(product.id)) {
                  // if this distance method option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!this.state.hiddenDistanceMethods.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== product.id);
                  }
                } else {
                  // if this distance method option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (this.state.hiddenDistanceMethods.includes(option.id)) {
                    option.hiddenProducts.push(product.id);
                  }
                }
              }

              break;
            case AccessReportsAccessAnalysisElementEnum.AccessStandard:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(product.id)) {
                  // if this access standard option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!this.state.hiddenAccessStandards.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== product.id);
                  }
                } else {
                  // if this access standard option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (this.state.hiddenAccessStandards.includes(option.id)) {
                    option.hiddenProducts.push(product.id);
                  }
                }
              }

              break;
            default:
              break;
          }
        }
      }

      if (!!coverPage) {
        // for string inputs, defaults at a per-product level will be stored in options,
        // with each option being a different product and the name/display name being the input values
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));

        for (const element of coverPageElements) {
          switch (element.elementId) {
            case AccessReportsCoverPageElementEnum.Product:
              product.previousElementFilterId = this.state.productType.id;
              product.groups = this.state.groups;

              if (!!product.accessCriterion && !product.skipAccessAnalysis) {
                for (const accessCriteria of product.accessCriterion) {
                  accessCriteria.filterMemberSetByOutsideServiceArea = this.state.filterMemberSetByOutsideServiceArea;
                  accessCriteria.memberSetServiceArea = this.state.selectedMemberSetServiceArea;

                  if (!!this.state.selectedMemberSetServiceArea &&
                    this.state.selectedMemberSetServiceArea.serviceAreaId !== null &&
                    this.state.selectedMemberSetServiceArea.serviceAreaId !== undefined
                  ) {
                    accessCriteria.filterMemberSetByServiceArea = true;
                  } else {
                    accessCriteria.filterMemberSetByServiceArea = false;
                  }
                }
              }

              const existingProduct: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

              if (!!existingProduct) {
                const productIndex: number = element.options.indexOf(existingProduct);
                element.options[productIndex] = product;
              } else {
                element.options.push(product);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleOne:
              const reportTitleOneOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

              if (!!reportTitleOneOption) {
                reportTitleOneOption.name = this.state.reportTitleOne;
                reportTitleOneOption.displayName = this.state.reportTitleOne;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: this.state.reportTitleOne,
                  hiddenProducts: [],
                  id: product.id,
                  name: this.state.reportTitleOne,
                };

                element.options.push(newOption);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleTwo:
              const reportTitleTwoOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

              if (!!reportTitleTwoOption) {
                reportTitleTwoOption.name = this.state.reportTitleTwo;
                reportTitleTwoOption.displayName = this.state.reportTitleTwo;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: this.state.reportTitleTwo,
                  hiddenProducts: [],
                  id: product.id,
                  name: this.state.reportTitleTwo,
                };

                element.options.push(newOption);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleThree:
              const reportTitleThreeOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

              if (!!reportTitleThreeOption) {
                reportTitleThreeOption.name = this.state.reportTitleThree;
                reportTitleThreeOption.displayName = this.state.reportTitleThree;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: this.state.reportTitleThree,
                  hiddenProducts: [],
                  id: product.id,
                  name: this.state.reportTitleThree,
                };

                element.options.push(newOption);
              }

              break;
            default:
              break;
          }
        }
      }

      this.props.onSaveProductClick(template);

      // redirect to dashboard
      this.props.history.push(`/access-reports/admin/dashboard`);
    }
  }

  public onSaveSpecialtiesClick = (specialties: AccessReportsWizardOption[]): void => {
    const product: AccessReportsWizardProduct = this.state.product;
    product.accessCriterion = specialties as AccessReportsWizardAccessCriteria[];

    this.setState({
      product,
    });
  }

  public onMemberSetServiceAreaDropdownChange = (selectedMemberSetServiceArea: AccessReportsServiceArea): void => {
    this.setState({
      selectedMemberSetServiceArea,
    });
  }

  public onProviderSetServiceAreaDropdownChange = (product: AccessReportsWizardProduct): void => {
    this.setState({
      product,
    });
  }

  public onSaveSpecialtyNameClick = (accessCriteriaId: number, accessCriteriaName: string): void => {
    if (!!this.state.product) {
      const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
      const accessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        accessCriteria.name = accessCriteriaName;

        this.setState({
          product,
        });
      }
    }
  }

  public onSkipAccessAnalysisToggle = (): void => {
    let template: AccessReportsWizardTemplate = cloneDeep(this.state.template);
    const product: AccessReportsWizardProduct = cloneDeep(this.state.product);
    product.skipAccessAnalysis = !product.skipAccessAnalysis;
    let groups: string[] = this.state.groups;
    let hiddenAccessStandards: number[] = this.state.hiddenAccessStandards;
    let hiddenCalculationMethods: number[] = this.state.hiddenCalculationMethods;
    let hiddenDistanceMethods: number[] = this.state.hiddenDistanceMethods;

    const handleSplitOptions = (): void => {
      const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions);
      const splitOptions: AccessReportsPageOptionsElementEnum[] = [AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard, AccessReportsPageOptionsElementEnum.SplitPagesBySpecialty];

      for (const split of splitOptions) {
        const splitElement = pageOptionsFormGroup.elements.find(x => x.elementId === split);
        const splitOption: AccessReportsWizardOption = splitElement?.options.find(x => x.id === this.state.product.id);

        if (product.skipAccessAnalysis) {
          // if skipping access analysis, remove defaulting the split option
          // and add the product to the list of hidden products for the split options
          splitOption.defaultProducts = splitOption.defaultProducts.filter(x => x !== this.state.product.id);

          if (!splitOption.hiddenProducts.includes(this.state.product.id)) {
            splitOption.hiddenProducts.push(this.state.product.id);
          }
        } else {
          // if the access analysis is not being skipped,
          // remove the product from the list of hidden products for the split option
          splitOption.hiddenProducts = splitOption.hiddenProducts.filter(x => x !== this.state.product.id);
        }
      }
    }

    const handlePages = (): void => {
      const groupPropertyIds = Object.values(AccessReportsWizardGroupPropertyIdEnum).filter(x => x !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages);

      for (const groupPropertyId of groupPropertyIds) {
        const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

        if (product.skipAccessAnalysis) {
          // if skipping access analysis, remove the product from the by-product defaults, column defaults, and multiple products
          // while also making sure to hide the page for this product
          pageFormGroup.byProductDefaults = pageFormGroup?.byProductDefaults.filter(x => x.productId !== this.state.product.id);
          pageFormGroup.columnDefaults = pageFormGroup?.columnDefaults.filter(x => x.productId !== this.state.product.id);
          pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== this.state.product.id);

          if (!pageFormGroup.hiddenProducts.includes(this.state.product.id)) {
            pageFormGroup.hiddenProducts.push(this.state.product.id);
          }
        } else {
          // if the access analysis is not being skipped,
          // remove the product from the list of hidden products for the page
          pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== this.state.product.id);
        }
      }
    }

    if (product.skipAccessAnalysis) {
      // whenever is access analysis required is checked:
      // 1.) create singular fake access criterion that will carry the provider set information
      // 2.) clear groups and access analysis page dropdowns defaults, as there are no real access criterion to assign to groups
      // 3.) hide and remove defaults from the page options skips
      // 4.) remove all non-provider count pages and page-speciic properties from being stored in the template to conserve space
      // 5.) create a base for provider count page
      product.accessCriterion = [this.getNewAccessCriterion("", 0, false)];
      product.groups = [];
      groups = [];

      const accessAnalysisPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);

      if (!!accessAnalysisPage) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

        for (const element of accessAnalysisPageElements) {
          for (const option of element.options) {
            option.hiddenProducts = option.hiddenProducts.filter(x => x !== this.state.product.id);
            option.defaultProducts = option.defaultProducts.filter(x => x !== this.state.product.id);
          }
        } 
      }

      hiddenAccessStandards = hiddenAccessStandards.filter(x => x !== this.state.product.id);
      hiddenCalculationMethods = hiddenCalculationMethods.filter(x => x !== this.state.product.id);
      hiddenDistanceMethods = hiddenDistanceMethods.filter(x => x !== this.state.product.id);

      handleSplitOptions();
      handlePages();

      template = this.buildNewPagesForProduct(template, this.state.product.id, true);
    } else {
      // whenever is access analysis required is unchecked:
      // 1.) clear access criterion, as unchecking means that the array only consisted of a fake one
      // 2.) create a base for all pages and page-specific properties
      // 3.) unhide the split page options
      product.accessCriterion = [];
      template = this.buildNewPagesForProduct(template, this.state.product.id, false);

      handleSplitOptions();
      handlePages();
    }

    this.setState({
      groups,
      hiddenAccessStandards,
      hiddenCalculationMethods,
      hiddenDistanceMethods,
      product,
      template,
    });
  }

  private buildNewPagesForProduct = (template: AccessReportsWizardTemplate, productId: number, skipAccessAnalysis: boolean): AccessReportsWizardTemplate => {
    // if skipping the access analysis, the only "page" we want to display is the provider count page
    const groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[] = skipAccessAnalysis ?
      [AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages] :
      Object.values(AccessReportsWizardGroupPropertyIdEnum);

    for (const groupPropertyId of groupPropertyIds) {
      const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);
      const byProductDefault: ByProductDefault = pageFormGroup?.byProductDefaults.find(x => x.productId === productId);
      // each page type should at least have the option to add a page or modify
      if (!byProductDefault) {
        const newByProductDefault: ByProductDefault = this.createByProductDefault(pageFormGroup, false, productId);
        pageFormGroup.byProductDefaults.push(newByProductDefault);
      }

      const productColumnDefaults: ColumnDefaults = pageFormGroup?.columnDefaults.find(x => x.productId === productId);

      if (!productColumnDefaults) {
        const genericColumnDefaults: ColumnDefaults = pageFormGroup?.columnDefaults.find(x => isNil(x.productId));
        const columnDefaults: ColumnDefaults = {
          productId,
          selectedColumns: !!genericColumnDefaults ? cloneDeep(genericColumnDefaults.selectedColumns) : [],
        };

        pageFormGroup.columnDefaults.push(columnDefaults);
      }
    }

    return template;
  }

  public setWizardStep = (newPage: number): void => {
    this.props.history.push(`/access-reports/admin/product/${this.state.product?.id}/step/${newPage + 1}`);
  }

  private createWizardStep = (
    wizardStep: AccessReportsAdminAddProductStepEnum,
    currentWizardStep: number,
    nextPath?: string,
    onSaveProductClick?: () => void,
    previousPath?: string,
  ): React.ReactNode => {
    let nextPathDisabled = true;
    let nextPathDisabledTooltipText = "Error";
    let redirect = false;
    let step: React.ReactNode = null;

    if (!this.props.template || !this.state.product) {
      step = (
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      );
    } else {
      // at least one option must be defaulted for each access analysis type
      let calculationMethodValid = false;
      let distanceMethodValid = false;
      let accessStandardValid = false;
      let calculationMethodHasDefault = false;
      let distanceMethodHasDefault = false;
      let accessStandardHasDefault = false;

      if (!this.state.product.skipAccessAnalysis) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = getAllElementsByPageFromTemplate(this.state.template, AccessReportsWizardStepEnum.AccessAnalysis);

        for (const element of accessAnalysisPageElements) {
          const defaultProductsByOption = element.options.map(x => x.defaultProducts);
          let hasDefault = false;
          for (const defaultProducts of defaultProductsByOption) {
            if (defaultProducts.includes(this.state.product?.id)) {
              hasDefault = true;
              break;
            }
          }

          if (hasDefault) {
            // do not allow the admin to hide all options from the end user
            switch (element.elementId) {
              case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
                calculationMethodValid = this.state.hiddenCalculationMethods.length < element.options.length;
                calculationMethodHasDefault = hasDefault;
                break;
              case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
                distanceMethodValid = this.state.hiddenDistanceMethods.length < element.options.length;
                distanceMethodHasDefault = hasDefault;
                break;
              case AccessReportsAccessAnalysisElementEnum.AccessStandard:
                accessStandardValid = this.state.hiddenAccessStandards.length < element.options.length;
                accessStandardHasDefault = hasDefault;
                break;
              default:
                break;
            }
          }
        }
      }

      const invalidSpecialty: AccessReportsWizardAccessCriteria = this.state.product?.accessCriterion.find(x => !this.getSpecialtyValidity(x))
      switch (wizardStep) {
        case AccessReportsAdminAddProductStepEnum.ProductDetails:
          if (
            !!this.state.product &&
            !!this.state.product.displayName &&
            !!this.state.product.name &&
            !!this.state.productType &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && this.state.groups.length > 0)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && calculationMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && distanceMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && accessStandardValid)) &&
            this.state.invalidElements.size === 0
          ) {
            nextPathDisabled = false;
          }

          if (!this.state.product?.skipAccessAnalysis) {
            if (!this.state.groups || this.state.groups.length === 0) {
              nextPathDisabledTooltipText = "Please input at least one group";
            }

            if (!accessStandardHasDefault) {
              nextPathDisabledTooltipText = "Please select a default access standard"
            }

            if (!accessStandardValid) {
              nextPathDisabledTooltipText = "Please do not hide all access standard options";
            }

            if (!distanceMethodHasDefault) {
              nextPathDisabledTooltipText = "Please select a default distance method"
            }

            if (!distanceMethodValid) {
              nextPathDisabledTooltipText = "Please do not hide all distance method options";
            }

            if (!calculationMethodHasDefault) {
              nextPathDisabledTooltipText = "Please select a default calculation method"
            }

            if (!calculationMethodValid) {
              nextPathDisabledTooltipText = "Please do not hide all calculaton method options";
            }
          }

          if (this.state.invalidElements.size > 0) {
            nextPathDisabledTooltipText = "Please correct the fields with invalid values";
          }

          if (!this.state.product.displayName || !this.state.product.name) {
            nextPathDisabledTooltipText = "Please input a product name";
          }

          if (!this.state.productType) {
            nextPathDisabledTooltipText = "Please input a product type";
          }

          step = (
            <AccessReportsAdminAddProductDetails
              disableUserActions={this.props.disableUserActions}
              groups={this.state.groups}
              hiddenCalculationMethods={this.state.hiddenCalculationMethods}
              hiddenDistanceMethods={this.state.hiddenDistanceMethods}
              hiddenAccessStandards={this.state.hiddenAccessStandards}
              invalidElements={this.state.invalidElements}
              onAccessAnalysisDefaultChange={this.onAccessAnalysisDefaultChange}
              onAccessAnalysisHideChange={this.onAccessAnalysisHideChange}
              onAddGroupClick={this.onAddGroupClick}
              onAllowEditToggle={this.onAllowEditToggle}
              onProductInputChange={this.onProductInputChange}
              onProductTypeChange={this.onProductTypeChange}
              onRemoveGroupClick={this.onRemoveGroupClick}
              onReportTitleInputChange={this.onReportTitleInputChange}
              onSkipAccessAnalysisToggle={this.onSkipAccessAnalysisToggle}
              product={this.state.product}
              productType={this.state.productType}
              productTypes={this.state.productTypes}
              reportTitleOne={this.state.reportTitleOne}
              reportTitleTwo={this.state.reportTitleTwo}
              reportTitleThree={this.state.reportTitleThree}
              template={this.state.template}
            />
          );
          break;
        case AccessReportsAdminAddProductStepEnum.Specialties:
          if (
            !!this.state.product &&
            !!this.state.product.displayName &&
            !!this.state.product.name &&
            !!this.state.productType &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && this.state.groups.length > 0)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && calculationMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && distanceMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && accessStandardValid))
          ) {
            if (this.state.product.accessCriterion.length > 0 && !invalidSpecialty) {
              nextPathDisabled = false;
            }

            if (invalidSpecialty) {
              nextPathDisabledTooltipText = "Please finish adding all necessary information to each specialty";
            }

            if (this.state.product && this.state.product.accessCriterion.length < 1) {
              nextPathDisabledTooltipText = "Please add at least one specialty";
            }

            step = (
              <AccessReportsAdminAddProductSpecialties
                disableUserActions={this.props.disableUserActions}
                filterMemberSetByOutsideServiceArea={this.state.filterMemberSetByOutsideServiceArea}
                groups={this.state.groups}
                onFilterMemberSetByOutsideServiceAreaToggle={this.onFilterMemberSetByOutsideServiceAreaToggle}
                onAddSpecialtiesClick={this.onAddSpecialtiesClick}
                onFilterProviderSetByOutsideServiceAreaToggle={this.onFilterProviderSetByOutsideServiceAreaToggle}
                onGroupToggle={this.onGroupToggle}
                onMemberSetServiceAreaDropdownChange={this.onMemberSetServiceAreaDropdownChange}
                onNetworkStandardChange={this.onNetworkStandardChange}
                onNetworkStandardMilesChange={this.onNetworkStandardMilesChange}
                onProviderGroupChange={this.onProviderGroupChange}
                onProviderSetServiceAreaDropdownChange={this.onProviderSetServiceAreaDropdownChange}
                onRemoveSpecialtyClick={this.onRemoveSpecialtyClick}
                onSaveSpecialtiesClick={this.onSaveSpecialtiesClick}
                onSaveSpecialtyNameClick={this.onSaveSpecialtyNameClick}
                product={this.state.product}
                selectedMemberSetServiceArea={this.state.selectedMemberSetServiceArea}
              />
            );
          } else {
            redirect = true;
          }

          break;
        case AccessReportsAdminAddProductStepEnum.PageOptions:
          if (
            !!this.state.product &&
            !!this.state.product.displayName &&
            !!this.state.product.name &&
            !!this.state.productType &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && this.state.groups.length > 0)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && calculationMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && distanceMethodValid)) &&
            (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && accessStandardValid)) &&
            this.state.product.accessCriterion.length > 0 &&
            !invalidSpecialty
          ) {
            let radiiOneMissing = false;
            const radiusSearchFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.state.template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch);
            const radiusByProductDefaults: ByProductDefault[] = radiusSearchFormGroup?.byProductDefaults.filter(x => x.productId === this.state.product.id);

            for (const radiusByProductDefault of radiusByProductDefaults) {
              if (!radiusByProductDefault[AccessRadiusSearchPageElementEnum.RadiiOne]) {
                radiiOneMissing = true;
                break;
              }
            }

            const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.state.template, AccessReportsWizardStepEnum.PageOptions);
            const legalDisclaimerElement: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);
            const productOption: AccessReportsWizardOption = legalDisclaimerElement?.options.find(x => x.id === this.state.product?.id);

            // the legal disclaimer section is considered to be valid if
            // the legal disclaimer has been defaulted and there is text present in the textarea field OR
            // the legal disclaimer has not been defaulted (text present irrelevant)

            // the following only applies if the is access analysis required checkbox is not checked:
            // the access radius search section is considered to be valid if
            // a radius search page form group is not present OR a radius search page form group IS present and
            // the page type is hidden OR it is not hidden and contains at least one radii value AND
            // each radii must be in ascending order
            if (((productOption?.defaultProducts.includes(this.state.product?.id) && productOption?.name !== "") ||
              (!productOption?.defaultProducts.includes(this.state.product?.id)) &&
              this.state.invalidPageOptionPageElements.size === 0) && (this.state.product.skipAccessAnalysis || (!this.state.product.skipAccessAnalysis && (
                !!radiusSearchFormGroup?.hiddenProducts.includes(this.state.product.id) ||
                (!radiusSearchFormGroup?.hiddenProducts.includes(this.state.product.id) && !radiiOneMissing)
              )))) {
              nextPathDisabled = false;
            }

            // access radius search page validity
            if (!this.state.product.skipAccessAnalysis && this.state.invalidPageOptionPageElements.size > 0) {
              nextPathDisabledTooltipText = "Access Search Radius Page radii values must be in ascending order";
            }

            if (!this.state.product.skipAccessAnalysis && !radiusSearchFormGroup?.hiddenProducts.includes(this.state.product.id) && radiiOneMissing) {
              nextPathDisabledTooltipText = "Please input at least one radii if not hiding the radius search page";
            }

            if ((productOption?.defaultProducts.includes(this.state.product?.id) && productOption?.name === "")) {
              nextPathDisabledTooltipText = "Please input legal disclaimer text if defaulting the legal disclaimer checkbox";
            }

            step = (
              <AccessReportsAdminAddProductPageOptions
                disableUserActions={this.props.disableUserActions}
                invalidPageOptionPageElements={this.state.invalidPageOptionPageElements}
                onAddPageClick={this.onAddPageClick}
                onDisclaimerTextChange={this.onDisclaimerTextChange}
                onPageOptionPageChange={this.onPageOptionPageChange}
                onPageOptionPageToggle={this.onPageOptionPageToggle}
                onPageOptionToggle={this.onPageOptionToggle}
                onRemoveFormGroupClick={this.onRemoveFormGroupClick}
                onSaveAccessOverviewChartColorsClick={this.onSaveAccessOverviewChartColorsClick}
                onSaveAccessSummaryChartColorsClick={this.onSaveAccessSummaryChartColorsClick}
                onSaveColumnsClick={this.onSaveColumnsClick}
                product={this.state.product}
                template={this.state.template}
              />
            );
          } else {
            redirect = true;
          }

          break;
        default:
          console.error("Invalid Access Reports Admin Step");
      }
    }

    if (!!this.state.product && redirect) {
      return <Redirect to={`/access-reports/admin/product/${this.state.product.id}/step/1`} />;
    } else {
      return (
        <AccessReportsAdminWizardStep
          currentWizardStep={currentWizardStep}
          nextPath={nextPath}
          nextPathDisabled={nextPathDisabled}
          nextPathDisabledTooltipText={nextPathDisabledTooltipText}
          onSaveProductClick={onSaveProductClick}
          previousPath={previousPath}
          setWizardStep={this.setWizardStep}
          wizardSteps={Object.values(AccessReportsAdminAddProductStepEnum)}
        >
          {step}
        </AccessReportsAdminWizardStep>
      );
    }
  }

  private extractProductDefaults = (): void => {
    const accessAnalysisPage: AccessReportsWizardPage = this.props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);
    const coverPage: AccessReportsWizardPage = this.props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
    const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);

    let filterMemberSetByOutsideServiceArea = this.state.filterMemberSetByOutsideServiceArea;
    let groups: string[] = this.state.groups;
    let hiddenCalculationMethods: number[] = this.state.hiddenCalculationMethods;
    let hiddenDistanceMethods: number[] = this.state.hiddenDistanceMethods;
    let hiddenAccessStandards: number[] = this.state.hiddenAccessStandards;
    let product: AccessReportsWizardProduct = productElement?.options.find(x => x.id.toString() === this.props.productId.toString()) as AccessReportsWizardProduct;
    let productType: AccessReportsWizardOption = this.state.productType;
    let productTypes: AccessReportsWizardOption[] = this.state.productTypes;
    let reportTitleOne: string = this.state.reportTitleOne;
    let reportTitleTwo: string = this.state.reportTitleTwo;
    let reportTitleThree: string = this.state.reportTitleThree;
    let selectedMemberSetServiceArea: AccessReportsServiceArea = this.state.selectedMemberSetServiceArea;
    let template: AccessReportsWizardTemplate = cloneDeep(this.props.template);
    const pageOptionsPage: AccessReportsWizardPage = getPageFromTemplate(template, AccessReportsWizardStepEnum.PageOptions);

    if (!!product) {
      // editing a product that already exists within the template
      // align product name and display name
      if (product.displayName) {
        product.name = product.displayName;
      } else {
        product.displayName = product.name;
      }
    } else {
      // adding a new product
      product = this.getNewProductModel();
      product.id = this.props.productId;
    }

    if (!!accessAnalysisPage && !product.skipAccessAnalysis) {
      const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

      for (const element of accessAnalysisPageElements) {
        switch (element.elementId) {
          case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
            const hiddenCalculationMethodOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(product.id));

            if (hiddenCalculationMethodOptions.length) {
              hiddenCalculationMethods = hiddenCalculationMethodOptions.map(x => x.id);
            }

            break;
          case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
            const hiddenDistanceMethodOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(product.id));

            if (hiddenDistanceMethodOptions.length) {
              hiddenDistanceMethods = hiddenDistanceMethodOptions.map(x => x.id);
            }

            break;
          case AccessReportsAccessAnalysisElementEnum.AccessStandard:
            const hiddenAccessStandardOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(product.id));

            if (hiddenAccessStandardOptions.length) {
              hiddenAccessStandards = hiddenAccessStandardOptions.map(x => x.id);
            }

            break;
          default:
            console.error("Invalid Access Analysis Element");
            break;
        }
      }
    }

    if (!!coverPage) {
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));

      for (const element of coverPageElements) {
        switch (element.elementId) {
          case AccessReportsCoverPageElementEnum.ReportTitleOne:
            const reportTitleOneOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

            if (!!reportTitleOneOption) {
              reportTitleOne = reportTitleOneOption.displayName ? reportTitleOneOption.displayName : reportTitleOneOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.ReportTitleTwo:
            const reportTitleTwoOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

            if (!!reportTitleTwoOption) {
              reportTitleTwo = reportTitleTwoOption.displayName ? reportTitleTwoOption.displayName : reportTitleTwoOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.ReportTitleThree:
            const reportTitleThreeOption: AccessReportsWizardOption = element.options.find(x => x.id === product.id);

            if (!!reportTitleThreeOption) {
              reportTitleThree = reportTitleThreeOption.displayName ? reportTitleThreeOption.displayName : reportTitleThreeOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.Product:
            const productOption: AccessReportsWizardProduct = element.options.find(x => x.id === product.id) as AccessReportsWizardProduct;

            if (!!productOption) {
              // groups
              if (!!product.groups && product.groups.length) {
                groups = cloneDeep(productOption.groups);
              } else {
                for (const accessCriteria of productOption.accessCriterion) {
                  if (!!accessCriteria.groups) {
                    for (const group of accessCriteria.groups) {
                      if (!groups.includes(group)) {
                        groups.push(group);
                      }
                    }
                  }
                }
              }

              // member set service area
              if (!!product.accessCriterion && !product.skipAccessAnalysis) {
                const firstAccessCriterion: AccessReportsWizardAccessCriteria = product.accessCriterion[0];

                if (!!firstAccessCriterion) {
                  filterMemberSetByOutsideServiceArea = firstAccessCriterion.filterMemberSetByOutsideServiceArea;

                  if (!!firstAccessCriterion.memberSetServiceArea) {
                    selectedMemberSetServiceArea = firstAccessCriterion.memberSetServiceArea;
                  }
                }
              }
            }

            break;
          case AccessReportsCoverPageElementEnum.ProductType:
            const productTypeOption: AccessReportsWizardOption = element.options.find(x => product.previousElementFilterId !== null &&
              product.previousElementFilterId !== undefined &&
              x.id === product.previousElementFilterId
            );

            if (!!productTypeOption) {
              productType = productTypeOption;
            }

            if (!!element.options) {
              productTypes = element.options;
            }

            break;
          default:
            break;
        }
      }
    }

    if (!!pageOptionsPage) {
      const pageOptionsFormGroups: AccessReportsWizardFormGroup[] = pageOptionsPage.formGroups.filter(x => !x.groupPropertyId);

      if (pageOptionsFormGroups.length > 0) {
        const pageOptionElements: AccessReportsWizardElement[] = flatten(pageOptionsFormGroups.map(x => x.elements));

        for (const element of pageOptionElements) {
          const pageOptionProductOption: AccessReportsWizardOption = element.options?.find(x => x.id === product.id);

          if (!pageOptionProductOption) {
            const newOption: AccessReportsWizardOption = {
              defaultProducts: [],
              displayName: product.displayName,
              hiddenProducts: [],
              id: product.id,
              name: product.name,
            };

            // separator and disclaimer page nodes will always be hidden from access reports wizard users
            if (element.elementId === AccessReportsPageOptionsElementEnum.SeparatorPage ||
              element.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer
            ) {
              newOption.hiddenProducts = [product.id];
              newOption.displayName = "";
              newOption.name = "";
            }

            if (!element.options) {
              element.options = [newOption];
            } else {
              element.options.push(newOption);
            }
          }
        }
      }

      template = this.buildNewPagesForProduct(template, product.id, product.skipAccessAnalysis);
    }

    this.setState({
      filterMemberSetByOutsideServiceArea,
      groups,
      hiddenAccessStandards,
      hiddenCalculationMethods,
      hiddenDistanceMethods,
      product,
      productType,
      productTypes,
      reportTitleOne,
      reportTitleThree,
      reportTitleTwo,
      selectedMemberSetServiceArea,
      template,
    });
  }

  private getAccessStandardDefaultValues = (standardType: AccessReportsWizardNetworkStandardTypeEnum): AccessReportsWizardNetworkStandard => {
    switch (standardType) {
      case AccessReportsWizardNetworkStandardTypeEnum.Standard:
        return {
          miles: 10,
          minCount: 2,
          minutes: 20,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipUrban:
        return {
          miles: 5,
          minCount: 2,
          minutes: 10,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban:
        return {
          miles: 10,
          minCount: 2,
          minutes: 15,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipRural:
        return {
          miles: 25,
          minCount: 2,
          minutes: 40,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyLarge:
        return {
          miles: 5,
          minCount: 1,
          minutes: 10,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyMetro:
        return {
          miles: 10,
          minCount: 1,
          minutes: 15,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyMicro:
        return {
          miles: 20,
          minCount: 1,
          minutes: 30,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyRural:
        return {
          miles: 30,
          minCount: 1,
          minutes: 40,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC:
        return {
          miles: 60,
          minCount: 1,
          minutes: 70,
          type: standardType,
        };
      default:
        console.error("Invalid Standard Type");
    }
  }

  private getNewProductModel = (): AccessReportsWizardProduct => {
    const product: AccessReportsWizardProduct = {
      accessCriterion: [],
      defaultProducts: [],
      displayName: "",
      editable: false,
      groups: [],
      hiddenProducts: [],
      id: 0,
      lastUpdated: new Date(),
      name: "",
      previousElementFilterId: null,
      skipAccessAnalysis: false,
    };

    return product;
  }
}

export { AccessReportsAdminAddProduct };
